import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronDown } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../../../styling/stylingHelper';
import { useCostPriceCategoriesStore } from '../../../../stores/appCostPriceCategoriesStore';

interface CreateCustomCostPriceModalProps {
  restaurantId: string;
  onClose: () => void;
  initialCategoryId?: string;
  automationRunId?: string;
  onCreate: (name: string, price: number, priceExVat: number, categoryId: string, costPriceType?: string, automationRunId?: string) => Promise<void>;
}

const CreateCustomCostPriceModal = ({
  restaurantId,
  onClose,
  initialCategoryId,
  automationRunId,
  onCreate
}: CreateCustomCostPriceModalProps) => {
  const { t } = useTranslation();
  const categoriesStore = useCostPriceCategoriesStore();

  const [menuItemName, setMenuItemName] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [vatRate, setVatRate] = useState<'21' | '9'>('21');
  const [vatRateTouched, setVatRateTouched] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [costPriceType, setCostPriceType] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const selectVatRate = (rate: '21' | '9') => {
    setVatRate(rate);
    setVatRateTouched(true);
  };

  // Auto-default VAT from type unless the user has manually overridden.
  useEffect(() => {
    if (vatRateTouched) return;
    if (costPriceType === 'FOOD') setVatRate('9');
    else if (costPriceType === 'DRINKS') setVatRate('21');
  }, [costPriceType, vatRateTouched]);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      const categoriesStore = useCostPriceCategoriesStore.getState();
      if (!categoriesStore.isDataLoadedFor(restaurantId)) {
        await categoriesStore.load(restaurantId);
      }

      // Set default category if available
      const available = useCostPriceCategoriesStore.getState().categories;
      const defaultCategory = available.find(c => c.id === initialCategoryId) ?? available.find(c => c.isDefault);
      if (defaultCategory) {
        setSelectedCategory((current: any) => current ?? defaultCategory);
      }
    };

    loadCategories();
  }, [restaurantId, initialCategoryId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCreate = async () => {
    const priceValue = parseFloat(customPrice);

    if (!menuItemName.trim()) {
      alert(t('costPrice.createCustom.pleaseEnterName'));
      return;
    }

    if (isNaN(priceValue) || priceValue <= 0) {
      alert(t('costPrice.createCustom.pleaseEnterValidPrice'));
      return;
    }

    if (!selectedCategory) {
      alert(t('costPrice.createCustom.pleaseSelectCategory'));
      return;
    }

    setIsSaving(true);
    try {
      // Calculate ex-VAT price based on selected VAT rate
      const vatDivisor = vatRate === '21' ? 1.21 : 1.09;
      const exVatPrice = priceValue / vatDivisor;
      await onCreate(menuItemName.trim(), priceValue, exVatPrice, selectedCategory.id, costPriceType, automationRunId);
      onClose();
    } catch (error) {
      console.error('Failed to create custom cost price item:', error);
      alert(t('costPrice.createCustom.createFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const vatDivisor = vatRate === '21' ? 1.21 : 1.09;
  const calculatedExVat = customPrice ? (parseFloat(customPrice) / vatDivisor).toFixed(2) : '0.00';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/60" onClick={onClose}>
      <div
        className={cn(
          "bg-white dark:bg-background-dark rounded-xl shadow-xl w-full max-w-lg border",
          borderPrimary
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex justify-between items-start mb-4 relative">
            <div>
              <h2 className={cn(semiBoldText, largeTextSize, primaryText, "mb-1")}>
                {t('costPrice.createCustom.title')}
              </h2>
              <p className={cn(smallTextSize, secondaryText)}>
                {t('costPrice.createCustom.description')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="space-y-4 mb-6">
            {/* Menu item name input */}
            <div>
              <label htmlFor="menuItemName" className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                {t('costPrice.createCustom.menuItemName')} *
              </label>
              <input
                type="text"
                id="menuItemName"
                value={menuItemName}
                onChange={(e) => setMenuItemName(e.target.value)}
                className={cn(
                  "w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100",
                  borderPrimary,
                  whiteBG,
                  primaryText
                )}
                placeholder={t('costPrice.createCustom.menuItemNamePlaceholder')}
                disabled={isSaving}
              />
            </div>

            {/* VAT Rate Selector */}
            <div>
              <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                {t('costPrice.vatRate.label')} *
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => selectVatRate('21')}
                  disabled={isSaving}
                  className={cn(
                    "flex-1 px-4 py-3 rounded-xl border transition-colors",
                    vatRate === '21'
                      ? "border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-cardLightGreen-dark text-darkGreenColor dark:text-darkGreenColor-dark font-semibold"
                      : cn(borderPrimary, "bg-white hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark", secondaryText),
                    "disabled:opacity-50"
                  )}
                >
                  21%
                </button>
                <button
                  type="button"
                  onClick={() => selectVatRate('9')}
                  disabled={isSaving}
                  className={cn(
                    "flex-1 px-4 py-3 rounded-xl border transition-colors",
                    vatRate === '9'
                      ? "border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-cardLightGreen-dark text-darkGreenColor dark:text-darkGreenColor-dark font-semibold"
                      : cn(borderPrimary, "bg-white hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark", secondaryText),
                    "disabled:opacity-50"
                  )}
                >
                  9%
                </button>
              </div>
              <p className={cn("mt-1 text-xs", secondaryText)}>
                {t('costPrice.vatRate.helper')}
              </p>
            </div>

            {/* Custom price input */}
            <div>
              <label htmlFor="customPrice" className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                {t('costPrice.createCustom.menuPrice')} *
              </label>
              <div className="relative">
                <span className={cn("absolute left-4 top-1/2 -translate-y-1/2", secondaryText)}>
                  €
                </span>
                <input
                  type="number"
                  id="customPrice"
                  step="0.01"
                  min="0"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  className={cn(
                    "w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100",
                    borderPrimary,
                    whiteBG,
                    primaryText
                  )}
                  placeholder="0.00"
                  disabled={isSaving}
                />
              </div>
              <p className={cn("mt-1 text-xs", secondaryText)}>
                {t('costPrice.createCustom.priceIncludesVat', { rate: vatRate })}
              </p>
            </div>

            {/* Calculated ex-VAT price */}
            {customPrice && parseFloat(customPrice) > 0 && (
              <div className={cn("p-3 bg-gray-50 dark:bg-gray-800/30 rounded-xl border", borderPrimary)}>
                <p className={cn("text-xs mb-1", secondaryText)}>
                  {t('costPrice.createCustom.calculatedExVat')} ({vatRate}% VAT)
                </p>
                <p className={cn("text-sm font-medium", primaryText)}>
                  €{calculatedExVat}
                </p>
              </div>
            )}

            {/* Category Selection */}
            <div>
              <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                {t('costPrice.createCustom.category')} *
              </label>
              <div className="relative" ref={categoryDropdownRef}>
                <button
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  disabled={isSaving}
                  className={cn(
                    "w-full px-4 py-3 border rounded-xl text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 disabled:opacity-50",
                    borderPrimary,
                    whiteBG,
                    primaryText
                  )}
                >
                  <span>{selectedCategory ? selectedCategory.name : t('costPrice.createCustom.selectCategory')}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showCategoryDropdown && (
                  <div className={cn("absolute z-10 w-full mt-1 border rounded-xl shadow-lg max-h-60 overflow-y-auto", borderPrimary, whiteBG)}>
                    {categoriesStore.categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => {
                          setSelectedCategory(category);
                          setShowCategoryDropdown(false);
                        }}
                        className={cn(
                          "w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark transition-colors first:rounded-t-xl last:rounded-b-xl",
                          selectedCategory?.id === category.id && "bg-emerald-50 dark:bg-darkGreenColor-dark/20"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span className={cn("text-sm", primaryText)}>{category.name}</span>
                          {category.isDefault && (
                            <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-lg">
                              Default
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Type Selection (Food / Drinks / No type) */}
            <div>
              <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                {t('costPrice.type')}
              </label>
              <div className="flex gap-3">
                {[
                  { value: undefined, label: t('costPrice.noType'), color: 'gray' },
                  { value: 'FOOD', label: t('costPrice.food'), color: 'green' },
                  { value: 'DRINKS', label: t('costPrice.drinks'), color: 'blue' }
                ].map((option) => {
                  const isSelected = costPriceType === option.value;
                  const colorClasses = isSelected
                    ? option.color === 'green'
                      ? 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 font-semibold'
                      : option.color === 'blue'
                      ? 'border-blue-500 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold'
                      : 'border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-800/40 text-gray-700 dark:text-gray-300 font-semibold'
                    : cn(borderPrimary, "bg-white hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark", secondaryText);
                  return (
                    <button
                      key={option.value || 'none'}
                      type="button"
                      onClick={() => setCostPriceType(option.value)}
                      disabled={isSaving}
                      className={cn(
                        "flex-1 px-4 py-3 rounded-xl border transition-colors text-sm",
                        colorClasses,
                        "disabled:opacity-50"
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="-mx-4 mt-4">
            <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
            <div className="flex justify-end gap-3 pt-4 px-4">
              <button
                onClick={onClose}
                className={cn(
                  smallTextSize,
                  secondaryText,
                  semiBoldText,
                  "rounded-xl px-4 py-3",
                  whiteBG,
                  "border",
                  borderPrimary,
                  "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark",
                  "flex-1",
                  "disabled:opacity-50"
                )}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={isSaving || !menuItemName.trim() || !customPrice || parseFloat(customPrice) <= 0 || !selectedCategory}
                className={cn(
                  smallTextSize,
                  "text-white dark:text-white",
                  semiBoldText,
                  "bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-4 py-3",
                  "hover:bg-darkGreenColor/95",
                  "flex-1",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isSaving ? t('common.saving') : t('costPrice.createCustom.create')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCustomCostPriceModal;
